import { effectiveCriterion } from "../config/takeoffCriteria";
import { polygonOf, polygonArea, polygonPerimeter, DEFAULT_TUNABLES } from "./takeoffGeometry";
import { tryEvaluateFormula, evaluateFormula } from "./formulaEngine";
import { calcRoomSystems, roomSystemsFormulaVariables, DEFAULT_SYSTEMS_TUNABLES } from "./roomSystemsEngine";
import { DEFAULT_DUCT_TUNABLES } from "./takeoffMath";
import { DEFAULT_LAYOUT_TUNABLES } from "./autoLayoutEngine";
// Deliberate import cycle: roomElements.js evaluates an element's formula
// through computeRoomFormulaQty below, and the legacy-count variables below
// read a room's tagged elements through resolveLegacyCount. Both uses are
// inside functions (never at module evaluation), and resolveLegacyCount
// breaks the runtime recursion with ctx.legacyCountsFromCriteria.
import { resolveLegacyCount } from "../config/roomElements";

// Engine Variables (TakeoffVariables, db/65/68/69 — Setup ▸ Estimate Engine ▸
// Engine Variables) are bound into every room formula by their key
// (2026-09-11, per direct request: "I want formulas to be able to reference
// engine variables"), e.g. `floorArea * H * ach / 60 / HEPA_DEFAULT_DESIGN_CFM`.
// The built-in defaults are always present so a key resolves even for a
// caller that passes no ctx.tunables; the admin-edited live values in
// ctx.tunables win. Keys are ALL_CAPS by convention, which is also how the
// variable reference tells them apart from the camelCase room variables.
export function engineVariableBindings(tunables) {
  const out = {};
  for (const src of [DEFAULT_TUNABLES, DEFAULT_DUCT_TUNABLES, DEFAULT_SYSTEMS_TUNABLES, DEFAULT_LAYOUT_TUNABLES, tunables || {}]) {
    for (const [k, v] of Object.entries(src)) {
      const n = typeof v === "number" ? v : +v;
      if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(k) && Number.isFinite(n)) out[k] = n;
    }
  }
  return out;
}

// Phase 2 of the takeoff-to-pricing mapping (QuantitySourceType='room_formula',
// see db/49_estimate_design_rollup.sql's header note): a room's own
// dimensions + Discipline Criteria feed an admin-editable EstimateCostFormulas
// expression (formulaEngine.js — the same safe evaluator already trusted for
// circuit-route math and assembly quantity formulas) instead of reading a
// drawn condition. This is the flat variable-binding contract a formula
// author writes against; keep it in sync with the help text in
// src/pages/EstimateCostLibrary.jsx's Formulas tab.
//
// L/W/H/floorArea/perimeter mirror roomScopeFormulas.js's exact derivation
// (real traced shape via PolygonJson when present, else the bounding box).
//
// Engineering outputs (2026-09-11): hepaCount / supplyCfm / lightingFixtureCount
// / lightingWatts / ... come from roomSystemsEngine.js's calcRoomSystems() —
// the formula layer CONSUMES engineering results, it never rebuilds
// `floorArea*H*ach/60/500` itself. `ctx` is { bodAttrs, settings, tunables }
// (see that module); a caller that passes none still gets every geometry/
// criteria variable plus engineering values at the built-in defaults.
export function roomFormulaVariables(room, criteria, ctx = {}) {
  const L = +room.LengthFt || 0;
  const W = +room.WidthFt || 0;
  const H = +room.HeightFt || 0;
  const poly = polygonOf(room);
  const floorArea = poly ? polygonArea(poly) : L * W;
  const perimeter = poly ? polygonPerimeter(poly) : 2 * (L + W);

  const numField = (disc, fieldKey) => +effectiveCriterion(criteria, disc, fieldKey, room).value || 0;
  // A legacy count with a Room Element replacement (P0 Phase D migration):
  // the room's tagged elements win, the criterion is the fallback
  // (roomElements.js resolveLegacyCount — the same rule the circuit engine
  // and the Utility demand view apply). Doors / windows / pass-throughs stay
  // on the criterion until P1 gives openings a stable identity.
  const legacyCount = (fieldKey) => resolveLegacyCount(fieldKey, room, criteria, ctx).value;

  const systems = calcRoomSystems(room, criteria, ctx);

  return {
    ...engineVariableBindings(ctx.tunables), // first, so a room variable always wins a (never expected) name clash
    L, W, H, floorArea, perimeter,
    ach: +room.ACH || 0,
    doorCount: numField("arch", "door_count"),
    windowCount: numField("arch", "window_count"),
    passThroughs: numField("arch", "pass_throughs"),
    occupancyCount: numField("mech", "occupancy_count"),
    exteriorWallFt: numField("mech", "exterior_wall_ft"),
    outletCount: legacyCount("outlet_count"),
    cdaDrops: legacyCount("cda_drops"),
    n2Drops: legacyCount("n2_drops"),
    vacDrops: legacyCount("vac_drops"),
    pwDrops: legacyCount("pw_drops"),
    // Engineering layer — lightingFixtureCount is the EFFECTIVE count
    // (override ?? calculated ?? legacy fallback), no longer the raw criterion.
    ...roomSystemsFormulaVariables(systems),
  };
}

export function computeRoomFormulaQty(formula, room, criteria, ctx = {}) {
  return tryEvaluateFormula(formula.Expression, roomFormulaVariables(room, criteria, ctx));
}

// Why a room formula gave no number, in words an estimator can act on — "" when it resolves. The common trap: cfmPerHepa and
// hepaCount are ENGINE OUTPUTS, 0 until the room has a HEPA element the engine counts (an Item / Assembly tagged Generated
// system = HEPA), so a formula that divides by them divides by zero.
export function explainRoomFormulaFailure(formula, room, criteria, ctx = {}) {
  const vars = roomFormulaVariables(room, criteria, ctx);
  try {
    const v = evaluateFormula(formula.Expression, vars);
    return Number.isFinite(v) ? "" : "the result is not a number";
  } catch (e) {
    const msg = String(e.message || e).replace(/ in formula.*$/, "");
    if (/division by zero/i.test(msg)) {
      const zeroOutputs = ["cfmPerHepa", "hepaCount", "hepaFaceVelocity"].filter(n => new RegExp("\\b" + n + "\\b").test(formula.Expression || "") && !(vars[n] > 0));
      return zeroOutputs.length
        ? `it divides by zero: ${zeroOutputs.join(" and ")} ${zeroOutputs.length > 1 ? "are" : "is"} 0 for this room because the engine counts no HEPA here (only an Item / Assembly tagged "Generated system = HEPA" counts). Use hepaCount from a tagged element, or divide by HEPA_DEFAULT_DESIGN_CFM instead`
        : "it divides by zero for this room";
    }
    return msg;
  }
}
